import { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import type { CookDetails, MeatProperties } from '../../types';
import NumberInput from '../NumberInput';

interface Props {
  data: Partial<CookDetails>;
  onChange: (data: Partial<CookDetails>) => void;
  onNext: () => void;
}

export default function CookSettings({ data, onChange }: Props) {
  const [meatConfig, setMeatConfig] = useState<MeatProperties | null>(null);
  // Local raw string so the user can freely type in the smoker-temp text field
  const [smokerTempRaw, setSmokerTempRaw] = useState(String(data.smokerTempF || 225));

  // Keep text field in sync when slider (or external reset) changes smokerTempF
  useEffect(() => {
    setSmokerTempRaw(String(data.smokerTempF || 225));
  }, [data.smokerTempF]);

  // Load meat config
  useEffect(() => {
    if (data.meatType) {
      fetch('/config/meats.json')
        .then(res => res.json())
        .then((meats: MeatProperties[]) => {
          const meat = meats.find(m => m.type === data.meatType);
          if (meat) {
            setMeatConfig(meat);
            if (!data.targetTempF) {
              onChange({ targetTempF: meat.temperatures.targetInternalF });
            }
          }
        })
        .catch(err => console.error('Failed to load meat config:', err));
    }
  }, [data.meatType, data.targetTempF, onChange]);

  const recommendedTemp = meatConfig?.empirical?.recommendedSmokerTempF || 250;

  return (
    <div className="space-y-6">
      {/* Smoker Temperature — slider + direct text entry */}
      <div>
        <div className="flex items-baseline justify-between mb-4">
          <label className="text-sm font-medium text-smoke-700">Smoker Temperature</label>
          <div className="flex items-baseline gap-1">
            <input
              type="text"
              inputMode="numeric"
              value={smokerTempRaw}
              aria-label="Smoker temperature in degrees Fahrenheit"
              onChange={(e) => setSmokerTempRaw(e.target.value)}
              onBlur={() => {
                const num = parseInt(smokerTempRaw);
                if (!isNaN(num)) {
                  const clamped = Math.min(400, Math.max(225, Math.round(num / 5) * 5));
                  onChange({ smokerTempF: clamped });
                  setSmokerTempRaw(String(clamped));
                } else {
                  setSmokerTempRaw(String(data.smokerTempF || 225));
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              }}
              className="w-20 px-2 py-1 text-center text-xl font-bold text-flame-600 border border-smoke-300 rounded-lg focus:ring-2 focus:ring-flame-500 focus:border-flame-500 outline-none"
            />
            <span className="text-smoke-600 text-sm font-medium">°F</span>
          </div>
        </div>
        <div className="flex justify-between text-xs text-smoke-500 mb-1">
          <span>225°F — Low &amp; Slow</span>
          <span>275°F</span>
          <span>400°F — Hot &amp; Fast</span>
        </div>
        <input
          type="range"
          min="225"
          max="400"
          step="5"
          value={data.smokerTempF || 225}
          aria-label="Smoker temperature slider"
          onChange={(e) => {
            const val = parseInt(e.target.value);
            onChange({ smokerTempF: val });
            setSmokerTempRaw(String(val));
          }}
          className="w-full slider"
        />
        {data.meatType === 'beef-ribs' && meatConfig && (
          <p className="text-xs text-blue-700 mt-2 bg-blue-50 border border-blue-200 rounded px-2 py-1">
            💡 {recommendedTemp}°F is optimal for beef ribs — renders fat better without drying out. Our cook times are calibrated for {recommendedTemp}°F.
          </p>
        )}
      </div>

      {/* Target Internal Temperature ("done temp") */}
      <div>
        <label className="block text-sm font-medium text-smoke-700 mb-2">
          Done Temp — Target Internal Temperature (°F)
        </label>
        <NumberInput
          value={data.targetTempF}
          onChange={(val) => onChange({ targetTempF: val })}
          min={140}
          max={220}
          step={1}
          decimalPlaces={0}
          placeholder="Auto-set from meat type"
        />
        <p className="text-xs text-smoke-600 mt-2">
          140–220°F · Default set from meat type · Common: Poultry 165°F · Pork 145–203°F · Brisket 203°F
        </p>
      </div>

      {/* Target Serving Time */}
      <div>
        <label className="block text-sm font-medium text-smoke-700 mb-2">
          When do you want to serve/eat?
        </label>
        <DatePicker
          selected={data.targetServingTime ? new Date(data.targetServingTime) : null}
          onChange={(date: Date | null) => onChange({ targetServingTime: date || undefined })}
          showTimeSelect
          timeIntervals={15}
          timeCaption="Time"
          dateFormat="MMMM d, yyyy h:mm aa"
          minDate={new Date(Date.now() + 6 * 60 * 60 * 1000)}
          placeholderText="Click to select date and time"
          className="input-field w-full"
          calendarClassName="shadow-lg"
          showPopperArrow={false}
          autoComplete="off"
        />
        <p className="text-xs text-smoke-600 mt-1">
          We'll work backward from this time to calculate your start time and use the correct weather forecast window.
        </p>
      </div>

      {/* Spacing Arrangement - CRITICAL for multi-piece cooks */}
      {data.pieces && data.pieces.filter(p => p.weight > 0).length > 1 && (
        <div>
          <label className="block text-sm font-medium text-smoke-700 mb-2">
            Piece Arrangement in Smoker
          </label>
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => onChange({ 
                spacing: { arrangement: 'gaps', spacingFactor: 1.0 } 
              })}
              className={`w-full py-3 px-4 rounded-lg text-left border-2 transition-all ${
                data.spacing?.arrangement === 'gaps' || !data.spacing
                  ? 'border-flame-600 bg-flame-50 text-smoke-900'
                  : 'border-smoke-200 bg-white text-smoke-700 hover:border-smoke-300'
              }`}
            >
              <div className="font-semibold">Good Gaps (1.0x)</div>
              <div className="text-xs mt-1 opacity-80">
                Pieces 2-3" apart, good airflow - optimal cook time
              </div>
            </button>
            <button
              type="button"
              onClick={() => onChange({ 
                spacing: { arrangement: 'touching', spacingFactor: 1.5 } 
              })}
              className={`w-full py-3 px-4 rounded-lg text-left border-2 transition-all ${
                data.spacing?.arrangement === 'touching'
                  ? 'border-flame-600 bg-flame-50 text-smoke-900'
                  : 'border-smoke-200 bg-white text-smoke-700 hover:border-smoke-300'
              }`}
            >
              <div className="font-semibold">Touching (1.5x) — +50% cook time</div>
              <div className="text-xs mt-1 opacity-80">
                Pieces touching sides, restricted airflow
              </div>
            </button>
            <button
              type="button"
              onClick={() => onChange({ 
                spacing: { arrangement: 'crowded', spacingFactor: 2.2 } 
              })}
              className={`w-full py-3 px-4 rounded-lg text-left border-2 transition-all ${
                data.spacing?.arrangement === 'crowded'
                  ? 'border-flame-600 bg-flame-50 text-smoke-900'
                  : 'border-smoke-200 bg-white text-smoke-700 hover:border-smoke-300'
              }`}
            >
              <div className="font-semibold">Crowded/Packed (2.2x) — +120% cook time!</div>
              <div className="text-xs mt-1 opacity-80">
                Packed tight, limited airflow - significantly longer cook
              </div>
            </button>
          </div>
          <p className="text-xs text-smoke-600 mt-2">
            ⚠️ Spacing is the dominant variable for multi-piece cooks. Crowded pieces can more than double your cook time!
          </p>
        </div>
      )}

      {/* Wrapping */}
      <div>
        <label className="flex items-center gap-2 mb-3">
          <input
            type="checkbox"
            checked={data.wrapping?.enabled || false}
            onChange={(e) =>
              onChange({
                wrapping: { 
                  enabled: e.target.checked, 
                  wrapType: data.wrapping?.wrapType || 'foil',
                  atTempF: data.wrapping?.atTempF || 160 
                },
              })
            }
            className="w-5 h-5 rounded border-smoke-300 text-flame-600 focus:ring-flame-500"
          />
          <span className="font-medium text-smoke-900">Wrap meat during cook</span>
        </label>
        {data.wrapping?.enabled && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-smoke-600 mb-2">Wrap Type</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => onChange({ 
                    wrapping: { 
                      enabled: true, 
                      wrapType: 'foil', 
                      atTempF: data.wrapping?.atTempF || 160 
                    } 
                  })}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                    data.wrapping.wrapType === 'foil' || !data.wrapping.wrapType
                      ? 'bg-flame-600 text-white'
                      : 'bg-smoke-100 text-smoke-700 hover:bg-smoke-200'
                  }`}
                >
                  Foil
                </button>
                <button
                  type="button"
                  onClick={() => onChange({ 
                    wrapping: { 
                      enabled: true, 
                      wrapType: 'butcher-paper', 
                      atTempF: (data.wrapping?.atTempF) || 160 
                    } 
                  })}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                    data.wrapping.wrapType === 'butcher-paper'
                      ? 'bg-flame-600 text-white'
                      : 'bg-smoke-100 text-smoke-700 hover:bg-smoke-200'
                  }`}
                >
                  Butcher Paper
                </button>
              </div>
              <p className="text-xs text-smoke-500 mt-1">
                Foil: Maximum heat retention (faster). Paper: Allows bark development (slower)
              </p>
            </div>
            <div>
              <label className="block text-xs text-smoke-600 mb-2">Wrap at temperature (°F)</label>
              <NumberInput
                value={data.wrapping.atTempF ?? 160}
                onChange={(val) => onChange({
                  wrapping: {
                    enabled: true,
                    wrapType: (data.wrapping?.wrapType) || 'foil',
                    atTempF: val ?? 160,
                  },
                })}
                min={140}
                max={200}
                step={5}
                decimalPlaces={0}
              />
              <p className="text-xs text-smoke-500 mt-2">140–200°F · Common: 160–165°F during stall</p>
            </div>
          </div>
        )}
      </div>

      {/* Spritz */}
      <div>
        <label className="flex items-center gap-2 mb-3">
          <input
            type="checkbox"
            checked={data.spritz?.enabled || false}
            onChange={(e) =>
              onChange({
                spritz: { 
                  enabled: e.target.checked, 
                  totalOpenings: data.spritz?.totalOpenings 
                },
              })
            }
            className="w-5 h-5 rounded border-smoke-300 text-flame-600 focus:ring-flame-500"
          />
          <span className="font-medium text-smoke-900">Spritz/mop periodically</span>
        </label>
        {data.spritz?.enabled && (
          <div>
            <label className="block text-xs text-smoke-600 mb-2">
              How many times will you open the lid to spritz/mop?
            </label>
            <NumberInput
              value={data.spritz.totalOpenings}
              onChange={(val) => onChange({
                spritz: { enabled: true, totalOpenings: val ?? undefined },
              })}
              min={1}
              max={50}
              step={1}
              decimalPlaces={0}
              placeholder="e.g. 6"
            />
            <p className="text-xs text-smoke-500 mt-2">
              1–50 openings · Each opening adds heat-loss time based on your smoker.
            </p>
          </div>
        )}
      </div>

      {/* Pan / Container */}
      <div>
        <label className="flex items-center gap-2 mb-3">
          <input
            type="checkbox"
            checked={data.pan?.enabled || false}
            onChange={(e) =>
              onChange({
                pan: {
                  enabled: e.target.checked,
                  type: data.pan?.type || 'aluminum-disposable',
                  elevated: data.pan?.elevated || false,
                },
              })
            }
            className="w-5 h-5 rounded border-smoke-300 text-flame-600 focus:ring-flame-500"
          />
          <span className="font-medium text-smoke-900">Using pans / containers</span>
        </label>
        <p className="text-xs text-smoke-500 -mt-1 mb-2">
          Pans block radiant heat and restrict airflow under the meat, adding 8-15% to cook time
        </p>
        {data.pan?.enabled && (
          <div className="space-y-3 ml-7">
            <div>
              <label className="block text-xs text-smoke-600 mb-1">Pan type</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 'aluminum-disposable', label: 'Aluminum Disposable' },
                  { value: 'aluminum-heavy', label: 'Aluminum Heavy-Duty' },
                  { value: 'cast-iron', label: 'Cast Iron' },
                  { value: 'stainless-steel', label: 'Stainless Steel' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() =>
                      onChange({
                        pan: {
                          enabled: true,
                          type: opt.value as 'aluminum-disposable' | 'aluminum-heavy' | 'cast-iron' | 'stainless-steel',
                          elevated: data.pan?.elevated || false,
                        },
                      })
                    }
                    className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                      data.pan?.type === opt.value
                        ? 'bg-flame-600 text-white'
                        : 'bg-smoke-100 text-smoke-700 hover:bg-smoke-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={data.pan?.elevated || false}
                onChange={(e) =>
                  onChange({
                    pan: {
                      enabled: true,
                      type: data.pan?.type || 'aluminum-disposable',
                      elevated: e.target.checked,
                    },
                  })
                }
                className="w-4 h-4 rounded border-smoke-300 text-flame-600 focus:ring-flame-500"
              />
              <span className="text-smoke-700">Rack insert (meat elevated above drippings)</span>
            </label>
            <p className="text-xs text-smoke-500">
              Elevating meat on a rack inside the pan restores some airflow underneath and reduces the pan penalty by ~45%
            </p>
          </div>
        )}
      </div>

      {/* Rest Time */}
      <div>
        <label className="block text-sm font-medium text-smoke-700 mb-2">
          Rest Time (hours)
        </label>
        <NumberInput
          value={data.restHours}
          onChange={(val) => onChange({ restHours: val ?? 1 })}
          min={0}
          max={8}
          step={0.5}
          decimalPlaces={1}
        />
        <p className="text-xs text-smoke-600 mt-2">
          0–8 hours · Typical: 1–2 hours wrapped in a cooler.
        </p>
      </div>

      {/* Exposure */}
      <div>
        <label className="block text-sm font-medium text-smoke-700 mb-3">
          Smoker Exposure to Weather
        </label>
        <div className="space-y-2">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={data.exposure?.useWeatherTemp ?? true}
              onChange={(e) =>
                onChange({
                  exposure: { 
                    useWeatherTemp: e.target.checked,
                    useWeatherWind: data.exposure?.useWeatherWind ?? true,
                    useWeatherPrecipitation: data.exposure?.useWeatherPrecipitation ?? true
                  },
                })
              }
              className="w-4 h-4 rounded border-smoke-300 text-flame-600 focus:ring-flame-500"
            />
            <span className="text-smoke-700">Fully exposed to weather</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={data.exposure?.useWeatherWind ?? true}
              onChange={(e) =>
                onChange({
                  exposure: { 
                    useWeatherTemp: data.exposure?.useWeatherTemp ?? true,
                    useWeatherWind: e.target.checked,
                    useWeatherPrecipitation: data.exposure?.useWeatherPrecipitation ?? true
                  },
                })
              }
              className="w-4 h-4 rounded border-smoke-300 text-flame-600 focus:ring-flame-500"
            />
            <span className="text-smoke-700">Exposed to wind</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={data.exposure?.useWeatherPrecipitation ?? true}
              onChange={(e) =>
                onChange({
                  exposure: { 
                    useWeatherTemp: data.exposure?.useWeatherTemp ?? true,
                    useWeatherWind: data.exposure?.useWeatherWind ?? true,
                    useWeatherPrecipitation: e.target.checked
                  },
                })
              }
              className="w-4 h-4 rounded border-smoke-300 text-flame-600 focus:ring-flame-500"
            />
            <span className="text-smoke-700">Exposed to rain/snow</span>
          </label>
        </div>
      </div>
    </div>
  );
}
